import { Fragment } from "react";
import { fmt2 } from "@/lib/api";
import { numLocale } from "@/lib/num";
import { fmtDate } from "@/components/DateInput";
import { fl, srcLabel } from "@/lib/finI18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, Download, RefreshCw, AlertTriangle } from "lucide-react";
import { DrillNum, useDrill } from "@/components/reports/Drill";

const n = (v) => fmt2(v || 0);

export function Empty({ lang }) {
  return <p className="text-center py-10 text-muted-foreground text-sm" data-testid="report-empty">{fl("empty", lang)}</p>;
}

export function BalanceBadge({ ok, lang, label }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="report-balance-status">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
      <span className={ok ? "text-emerald-600" : "text-rose-600"}>{label ? `${label} · ` : ""}{fl(ok ? "balanced" : "unbalanced", lang)}</span>
    </div>
  );
}

function SakNote({ lang, extra }) {
  return <p className="text-[11px] text-muted-foreground pt-2">{fl("sakNote", lang)}{extra ? ` ${extra}` : ""}</p>;
}

export function GLView({ report, mode, lang, onExportAccount }) {
  const drill = useDrill();
  if (!report.accounts.length) return <Card><Empty lang={lang} /></Card>;
  return (
    <div className="space-y-3" data-testid="gl-view">
      {report.accounts.map((a) => (
        <Card key={a.account_id} className="overflow-hidden" data-testid={`gl-account-${a.account_code}`}>
          <div className="flex items-center justify-between bg-secondary/50 px-4 py-2 gap-2 flex-wrap">
            <div className="min-w-0">
              <span className="font-semibold text-sm">{a.account_code} · {a.account_name}</span>
              <span className="ml-2 text-[11px] text-muted-foreground">{a.group_label}</span>
            </div>
            <div className="flex items-center gap-3 text-xs num">
              <span>{fl("opening", lang)} <span className="num" data-testid={`gl-opening-${a.account_code}`}>{n(a.opening)}</span></span>
              <span>{fl("debit", lang)} <DrillNum value={a.total_debit} q={{ account: a.account_id }} title={`${a.account_code} ${a.account_name}`} testid={`gl-drill-debit-${a.account_code}`} /></span>
              <span>{fl("credit", lang)} <DrillNum value={a.total_credit} q={{ account: a.account_id }} title={`${a.account_code} ${a.account_name}`} testid={`gl-drill-credit-${a.account_code}`} /></span>
              <span className="font-semibold">{fl("balance", lang)}: <DrillNum value={a.balance} q={{ account: a.account_id, basis: "cumulative" }} title={`${a.account_code} ${a.account_name}`} testid={`gl-drill-balance-${a.account_code}`} /></span>
              <Button variant="ghost" size="icon" className="h-7 w-7" title={fl("downloadPart", lang)} onClick={() => onExportAccount(a)} data-testid={`gl-export-${a.account_code}`}><Download className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {mode === "summary" ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{fl("ledger", lang)}</TableHead><TableHead className="text-right">{fl("opening", lang)}</TableHead><TableHead className="text-right">{fl("count", lang)}</TableHead>
                <TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead><TableHead className="text-right">{fl("net", lang)}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {a.ledgers.map((p) => (
                  <TableRow key={p.party} data-testid={`gl-ledger-${a.account_code}-${p.party}`}>
                    <TableCell className="text-xs">{p.party}</TableCell>
                    <TableCell className="num text-right text-xs">{n(p.opening)}</TableCell>
                    <TableCell className="num text-right text-xs">{p.count}</TableCell>
                    <TableCell className="text-right text-xs"><DrillNum value={p.debit} q={{ account: a.account_id, party: p.party }} title={`${a.account_code} · ${p.party}`} testid={`gl-led-debit-${a.account_code}-${p.party}`} /></TableCell>
                    <TableCell className="text-right text-xs"><DrillNum value={p.credit} q={{ account: a.account_id, party: p.party }} title={`${a.account_code} · ${p.party}`} testid={`gl-led-credit-${a.account_code}-${p.party}`} /></TableCell>
                    <TableCell className="text-right text-xs font-semibold"><DrillNum value={(p.opening || 0) + p.debit - p.credit} q={{ account: a.account_id, party: p.party, basis: "cumulative" }} title={`${a.account_code} · ${p.party}`} testid={`gl-led-net-${a.account_code}-${p.party}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("docNo", lang)}</TableHead><TableHead>{fl("ledger", lang)}</TableHead>
                <TableHead>{fl("desc", lang)}</TableHead><TableHead>{fl("source", lang)}</TableHead><TableHead>{fl("counter", lang)}</TableHead>
                <TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead><TableHead className="text-right">{fl("balance", lang)}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                <TableRow className="bg-secondary/30" data-testid={`gl-opening-row-${a.account_code}`}>
                  <TableCell colSpan={8} className="text-xs italic text-muted-foreground">{fl("opening", lang)}</TableCell>
                  <TableCell className="num text-right text-xs font-semibold">{n(a.opening)}</TableCell>
                </TableRow>
                {a.lines.map((l, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-secondary/50" data-testid={`gl-line-${a.account_code}-${i}`}
                    onClick={() => drill && drill({ doc: l.doc_id, title: `${l.invoice_no || srcLabel(l.source, lang)} · ${fmtDate(l.date)}` })}>
                    <TableCell className="num text-xs whitespace-nowrap">{fmtDate(l.date)}</TableCell>
                    <TableCell className="text-xs">{l.invoice_no || "—"}</TableCell>
                    <TableCell className="text-xs">{l.party || "—"}</TableCell>
                    <TableCell className="text-xs">{l.description}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground">{srcLabel(l.source, lang)}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[200px]">{l.counter || "—"}</TableCell>
                    <TableCell className="num text-right text-xs">{l.debit ? n(l.debit) : "—"}</TableCell>
                    <TableCell className="num text-right text-xs">{l.credit ? n(l.credit) : "—"}</TableCell>
                    <TableCell className="num text-right text-xs">{n(l.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      ))}
    </div>
  );
}

export function TBView({ report, mode, lang }) {
  const groups = Object.entries((report.rows || []).reduce((acc, r) => { (acc[r.group] = acc[r.group] || []).push(r); return acc; }, {})).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <Card className="p-4 space-y-3" data-testid="tb-view">
      <BalanceBadge ok={report.balanced} lang={lang} />
      <Table>
        <TableHeader><TableRow>
          <TableHead>{fl("code", lang)}</TableHead><TableHead>{fl("account", lang)}</TableHead>
          <TableHead className="text-right">{fl("opening", lang)}</TableHead><TableHead className="text-right">{fl("mutD", lang)}</TableHead><TableHead className="text-right">{fl("mutC", lang)}</TableHead>
          <TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {report.rows.length === 0 && <TableRow><TableCell colSpan={7}><Empty lang={lang} /></TableCell></TableRow>}
          {groups.map(([grp, rows]) => (
            <Fragment key={grp}>
              <TableRow className="bg-secondary/60" data-testid={`tb-group-${rows[0].group_key}`}>
                <TableCell colSpan={2} className="font-head font-semibold text-xs uppercase tracking-wider">{grp.replace(/^\d+ · /, "")}</TableCell>
                {["opening", "mutation_debit", "mutation_credit"].map((k) => <TableCell key={k} className="num text-right text-xs font-semibold">{n(rows.reduce((s, x) => s + (x[k] || 0), 0))}</TableCell>)}
                <TableCell className="text-right text-xs font-semibold"><DrillNum value={rows.reduce((s, x) => s + x.debit, 0)} q={{ account: rows.filter((x) => x.debit > 0).map((x) => x.account_id).join(",") || "none", basis: "cumulative", value: rows.reduce((s, x) => s + x.debit, 0) }} title={grp.replace(/^\d+ · /, "")} testid={`tb-drill-group-debit-${rows[0].group_key}`} /></TableCell>
                <TableCell className="text-right text-xs font-semibold"><DrillNum value={rows.reduce((s, x) => s + x.credit, 0)} q={{ account: rows.filter((x) => x.credit > 0).map((x) => x.account_id).join(",") || "none", basis: "cumulative", normal: "credit" }} title={grp.replace(/^\d+ · /, "")} testid={`tb-drill-group-credit-${rows[0].group_key}`} /></TableCell>
              </TableRow>
              {mode !== "summary" && rows.map((r) => (
                <TableRow key={r.account_id} data-testid={`tb-row-${r.account_code}`}>
                  <TableCell className="num text-xs pl-6">{r.account_code}</TableCell>
                  <TableCell className="text-xs">{r.account_name}</TableCell>
                  <TableCell className="num text-right text-xs">{n(r.opening)}</TableCell>
                  <TableCell className="num text-right text-xs">{n(r.mutation_debit)}</TableCell>
                  <TableCell className="num text-right text-xs">{n(r.mutation_credit)}</TableCell>
                  <TableCell className="text-right text-xs"><DrillNum dash value={r.debit} q={{ account: r.account_id, basis: "cumulative" }} title={`${r.account_code} ${r.account_name}`} testid={`tb-drill-debit-${r.account_code}`} /></TableCell>
                  <TableCell className="text-right text-xs"><DrillNum dash value={r.credit} q={{ account: r.account_id, basis: "cumulative", normal: "credit" }} title={`${r.account_code} ${r.account_name}`} testid={`tb-drill-credit-${r.account_code}`} /></TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
          <TableRow className="bg-secondary/50 font-semibold">
            <TableCell colSpan={2}>{fl("total", lang)}</TableCell>
            <TableCell className="num text-right">{n(report.total_opening)}</TableCell>
            <TableCell className="num text-right">{n(report.total_mutation_debit)}</TableCell>
            <TableCell className="num text-right">{n(report.total_mutation_credit)}</TableCell>
            <TableCell className="num text-right" data-testid="tb-total-debit">{n(report.total_debit)}</TableCell>
            <TableCell className="num text-right" data-testid="tb-total-credit">{n(report.total_credit)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
}

const CREDIT_SECS = new Set(["revenue", "other_income", "current_liabilities", "noncurrent_liabilities", "equity"]);
const CMP = {
  bs: [["opening", "opening"], ["move", "moveBal"], ["amount", "closingBal"], ["pct", "pctChange"]],
  pl: [["prior", "priorPeriod"], ["amount", "currentPeriod"], ["pct", "pctChange"]],
};
const pctOf = (cur, base) => {
  if (Math.abs(base || 0) < 0.005) return null;
  const r = (((cur || 0) - base) / Math.abs(base)) * 100;
  return (Math.sign(r) * Math.round(Math.abs(r) * 10 + 1e-9)) / 10;
};
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toLocaleString(numLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const baseOf = (x) => (x.opening !== undefined ? x.opening : x.prior);
const cv = (x, k, cur) => (k === "move" ? (cur || 0) - (x.opening || 0) : k === "amount" ? cur : x[k] || 0);
const COLW = "w-36 sm:w-44 shrink-0 whitespace-nowrap text-right pl-2";
const PCTW = "w-20 sm:w-24 shrink-0 whitespace-nowrap text-right pl-2";

function Pct({ cur, base, testid }) {
  const v = pctOf(cur, base);
  return <span className={`num ${PCTW} text-xs ${v == null ? "text-muted-foreground" : "text-foreground/80"}`} data-testid={testid}>{fmtPct(v)}</span>;
}

function Amts({ x, cur, cmp, children, testid }) {
  if (!cmp) return children ?? <span className="num">{n(cur)}</span>;
  return (
    <span className="flex shrink-0">
      {CMP[cmp].map(([k]) => (k === "pct" ? <Pct key={k} cur={cur} base={baseOf(x)} testid={testid ? `${testid}-pct` : undefined} /> : (
        <span key={k} className={`num ${COLW}`} data-testid={testid ? `${testid}-${k}` : undefined}>
          {k === "amount" ? children ?? n(cur) : n(cv(x, k, cur))}
        </span>
      )))}
    </span>
  );
}

function CmpHead({ cmp, lang }) {
  if (!cmp) return null;
  return (
    <div className="flex justify-end text-[10px] uppercase tracking-wider text-muted-foreground font-semibold border-b border-border pb-1" data-testid={`cmp-head-${cmp}`}>
      {CMP[cmp].map(([k, lbl]) => <span key={k} className={k === "pct" ? PCTW : COLW}>{fl(lbl, lang)}</span>)}
    </div>
  );
}

function accQ(a, basis, normal) {
  return a.account_id ? { account: a.account_id, basis, normal } : { types: "revenue,expense", basis, normal: "credit" };
}

function Sec({ sec, mode, lang, hideTotal, basis, cmp }) {
  if (!sec) return null;
  const normal = CREDIT_SECS.has(sec.key) ? "credit" : "debit";
  return (
    <div data-testid={`sec-${sec.key}`}>
      <h3 className="font-head font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{sec.label}</h3>
      {sec.groups.length === 0 && <p className="text-xs text-muted-foreground pl-3 py-1">—</p>}
      {sec.groups.map((g) => (
        <div key={g.key} className="mb-1">
          <div className="flex items-center justify-between gap-2 text-xs font-semibold bg-secondary/50 px-2 py-1 rounded">
            <span>{g.label}</span>
            <Amts x={g} cur={g.total} cmp={cmp}><DrillNum value={g.total} q={g.key === "current_earnings" ? { types: "revenue,expense", basis, normal: "credit" } : { account: g.accounts.map((a) => a.account_id).filter(Boolean).join(","), basis, normal }} title={g.label} testid={`sec-drill-group-${sec.key}-${g.key}`} /></Amts>
          </div>
          {mode !== "summary" && g.accounts.map((a) => (
            <div key={a.account_id || a.account_name} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-border/60 pl-4">
              <span className="text-muted-foreground min-w-0">{a.account_code} {a.account_name}</span>
              <Amts x={a} cur={a.amount} cmp={cmp} testid={cmp ? `sec-cmp-${sec.key}-${a.account_code || g.key}` : undefined}><DrillNum value={a.amount} q={accQ(a, basis, normal)} title={`${a.account_code || ""} ${a.account_name}`.trim()} testid={`sec-drill-acc-${sec.key}-${a.account_code || g.key}`} /></Amts>
            </div>
          ))}
        </div>
      ))}
      {!hideTotal && (
        <div className="flex items-center justify-between gap-2 font-semibold pt-1.5 border-t border-primary/30 text-sm">
          <span>{fl("total", lang)} {sec.label}</span><Amts x={sec} cur={sec.total} cmp={cmp} />
        </div>
      )}
    </div>
  );
}

function Subtotal({ label, value, strong, testid, negative, prior }) {
  const tone = (v) => (v >= 0 && !negative ? "text-emerald-600" : "text-rose-600");
  return (
    <div className={`flex items-center justify-between ${strong ? "border-t-2 border-primary pt-3 text-lg font-bold" : "border-y border-border py-2 font-semibold"}`} data-testid={testid}>
      <span>{label}</span>
      {prior === undefined ? <span className={`num ${tone(value)}`}>{n(value)}</span> : (
        <span className={`flex shrink-0 ${strong ? "text-base" : ""}`}>
          <span className={`num ${COLW} ${tone(prior)}`} data-testid={testid && `${testid}-prior`}>{n(prior)}</span>
          <span className={`num ${COLW} ${tone(value)}`}>{n(value)}</span>
          <Pct cur={value} base={prior} testid={testid && `${testid}-pct`} />
        </span>
      )}
    </div>
  );
}

function PLHorizontal({ s, report, mode, lang, cmp }) {
  const S = (k) => <Sec key={k} sec={s[k]} mode={mode} lang={lang} basis="period" cmp={cmp} />;
  const net = report.net_income;
  const pn = report.prior?.net_income;
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="pl-horizontal">
      <div className="space-y-4">
        <CmpHead cmp={cmp} lang={lang} />
        {["cost_of_revenue", "operating_expenses", "other_expenses", "income_tax"].map(S)}
        {net >= 0 && <Subtotal label={fl("netProfit", lang)} value={net} prior={pn} testid="pl-h-net-profit" />}
      </div>
      <div className="space-y-4">
        <CmpHead cmp={cmp} lang={lang} />
        {["revenue", "other_income"].map(S)}
        {net < 0 && <Subtotal label={fl("netLoss", lang)} value={-net} prior={pn === undefined ? undefined : -pn} negative testid="pl-h-net-loss" />}
      </div>
    </div>
  );
}

function CmpNote({ text, testid }) {
  return text ? <p className="text-[11px] text-muted-foreground" data-testid={testid}>{text}</p> : null;
}

export function PLView({ report, mode, lang, layout }) {
  const s = Object.fromEntries(report.sections.map((x) => [x.key, x]));
  const L = report.labels;
  const cmp = report.prior ? "pl" : undefined;
  const P = (k) => report.prior?.[k];
  const pp = report.prior_period;
  const note = pp && <CmpNote text={`${fl("priorRange", lang)}: ${pp.date_from} – ${pp.date_to}`} testid="pl-prior-range" />;
  if (layout === "horizontal") {
    return (
      <Card className="p-5 space-y-4 overflow-x-auto" data-testid="pl-view">
        {note}
        <div className={cmp ? "min-w-[680px] md:min-w-[1100px] space-y-4" : "space-y-4"}>
          <PLHorizontal s={s} report={report} mode={mode} lang={lang} cmp={cmp} />
          <div className="grid md:grid-cols-3 gap-x-8">
            <Subtotal label={L.gross_profit} value={report.gross_profit} prior={P("gross_profit")} testid="pl-gross-profit" />
            <Subtotal label={L.operating_profit} value={report.operating_profit} prior={P("operating_profit")} testid="pl-operating-profit" />
            <Subtotal label={L.profit_before_tax} value={report.profit_before_tax} prior={P("profit_before_tax")} testid="pl-pbt" />
          </div>
          <Subtotal label={L.net_income} value={report.net_income} prior={P("net_income")} strong testid="pl-net-income" />
        </div>
        <SakNote lang={lang} />
      </Card>
    );
  }
  const S = (k) => <Sec sec={s[k]} mode={mode} lang={lang} basis="period" cmp={cmp} />;
  return (
    <Card className="p-5 max-w-5xl space-y-4 overflow-x-auto" data-testid="pl-view">
      {note}
      <div className={cmp ? "min-w-[660px] space-y-4" : "space-y-4"}>
        <CmpHead cmp={cmp} lang={lang} />
        {S("revenue")}
        {S("cost_of_revenue")}
        <Subtotal label={L.gross_profit} value={report.gross_profit} prior={P("gross_profit")} testid="pl-gross-profit" />
        {S("operating_expenses")}
        <Subtotal label={L.operating_profit} value={report.operating_profit} prior={P("operating_profit")} testid="pl-operating-profit" />
        {S("other_income")}
        {S("other_expenses")}
        <Subtotal label={L.profit_before_tax} value={report.profit_before_tax} prior={P("profit_before_tax")} testid="pl-pbt" />
        {S("income_tax")}
        <Subtotal label={L.net_income} value={report.net_income} prior={P("net_income")} strong testid="pl-net-income" />
        <div className="flex items-center justify-between text-sm text-muted-foreground"><span>{L.oci}</span><span className="num">{n(report.oci)}</span></div>
        <div className="flex items-center justify-between text-sm font-semibold"><span>{L.tci}</span><span className="num">{n(report.total_comprehensive_income)}</span></div>
      </div>
      <SakNote lang={lang} />
    </Card>
  );
}

export function BSView({ report, mode, lang, layout }) {
  const L = report.labels;
  const ot = report.opening_totals;
  const cmp = ot ? "bs" : undefined;
  const Tot = ({ label, value, open, testid }) => (
    <div className="flex items-center justify-between gap-2 border-t-2 border-primary pt-2 font-bold" data-testid={testid}><span>{label}</span><span className={cmp ? "text-sm" : ""}><Amts x={{ opening: open }} cur={value} cmp={cmp} testid={cmp && testid} /></span></div>
  );
  const S = (sec) => <Sec key={sec.key} sec={sec} mode={mode} lang={lang} basis="cumulative" cmp={cmp} />;
  return (
    <Card className="p-5 space-y-4 overflow-x-auto" data-testid="bs-view">
      <div className="flex items-center gap-3 flex-wrap">
        <BalanceBadge ok={report.balanced} lang={lang} />
        {cmp && report.opening_date && <CmpNote text={`${fl("openingAt", lang)} ${fmtDate(report.opening_date)}`} testid="bs-opening-date" />}
      </div>
      <div className={layout === "horizontal" ? `grid md:grid-cols-2 gap-8 ${cmp ? "min-w-[700px] md:min-w-[1600px]" : ""}` : `space-y-6 ${cmp ? "max-w-5xl min-w-[700px]" : "max-w-2xl"}`}>
        <div className="space-y-4">
          <CmpHead cmp={cmp} lang={lang} />
          {report.asset_sections.map(S)}
          <Tot label={L.total_assets} value={report.total_assets} open={ot?.total_assets} testid="bs-total-assets" />
        </div>
        <div className="space-y-4">
          {layout === "horizontal" && <CmpHead cmp={cmp} lang={lang} />}
          {report.liability_sections.map(S)}
          <div className="flex items-center justify-between font-semibold text-sm"><span>{L.total_liabilities}</span><Amts x={{ opening: ot?.total_liabilities }} cur={report.total_liabilities} cmp={cmp} /></div>
          {S(report.equity_section)}
          <Tot label={L.total_le} value={report.total_liabilities + report.total_equity} open={ot ? ot.total_liabilities + ot.total_equity : 0} testid="bs-total-le" />
        </div>
      </div>
      <SakNote lang={lang} />
    </Card>
  );
}

export function CFView({ report, mode, lang }) {
  const L = report.labels;
  return (
    <Card className="p-5 max-w-3xl space-y-4" data-testid="cf-view">
      {report.sections.map((s) => (
        <div key={s.key} data-testid={`cf-${s.key}`}>
          <h3 className="font-head font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{s.label}</h3>
          {mode !== "summary" && s.accounts.map((a) => (
            <div key={a.account_id} className="flex items-center justify-between text-sm py-1 border-b border-border/60 pl-3">
              <span className="text-muted-foreground">{a.account_code} {a.account_name}</span>
              <DrillNum value={a.amount} q={{ account: a.account_id, normal: "credit" }} title={`${a.account_code} ${a.account_name}`} testid={`cf-drill-${s.key}-${a.account_code}`} className={a.amount >= 0 ? "" : "text-rose-600"} />
            </div>
          ))}
          <div className="flex items-center justify-between font-semibold pt-1.5 text-sm"><span>{fl("total", lang)}</span><span className="num">{n(s.total)}</span></div>
        </div>
      ))}
      <Subtotal label={L.net_cash} value={report.net} testid="cf-net" />
      <div className="flex items-center justify-between text-sm"><span>{L.cash_begin}</span><span className="num" data-testid="cf-begin">{n(report.begin_cash)}</span></div>
      <div className="flex items-center justify-between font-bold"><span>{L.cash_end}</span><span className="num" data-testid="cf-end">{n(report.end_cash)}</span></div>
      <SakNote lang={lang} extra={fl("cfNote", lang)} />
    </Card>
  );
}

export function RegisterView({ report, sales, lang, onSync }) {
  const drill = useDrill();
  return (
    <Card className="p-4 space-y-3" data-testid={sales ? "sales-register-view" : "purchase-register-view"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-muted-foreground max-w-2xl">{fl(sales ? "regSalesNote" : "regPurchNote", lang)}</p>
        {sales && onSync && <Button variant="outline" size="sm" onClick={onSync} data-testid="sync-invoice-journals"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />{fl("syncInv", lang)}</Button>}
      </div>
      <div className="overflow-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("docNo", lang)}</TableHead><TableHead>{fl(sales ? "customer" : "vendor", lang)}</TableHead>
            <TableHead>{fl("source", lang)}</TableHead><TableHead>{fl("account", lang)}</TableHead>
            <TableHead className="text-right">{fl("dppGoods", lang)}</TableHead><TableHead className="text-right">{fl("dppService", lang)}</TableHead>
            <TableHead className="text-right">{fl("dpp", lang)}</TableHead><TableHead className="text-right">{fl("ppn", lang)}</TableHead>
            <TableHead className="text-right">{fl("pph", lang)}</TableHead><TableHead className="text-right">{fl("total", lang)}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {report.rows.length === 0 && <TableRow><TableCell colSpan={11}><Empty lang={lang} /></TableCell></TableRow>}
            {report.rows.map((r) => (
              <TableRow key={r.doc_id} data-testid={`register-row-${r.doc_id}`} className="cursor-pointer hover:bg-secondary/50"
                onClick={() => drill && drill({ doc: r.doc_id, title: `${r.invoice_no || srcLabel(r.source, lang)} · ${r.party || ""}` })}>
                <TableCell className="num text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                <TableCell className="text-xs">{r.invoice_no || "—"}</TableCell>
                <TableCell className="text-xs">{r.party || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] whitespace-nowrap">{srcLabel(r.source, lang)}</Badge></TableCell>
                <TableCell className="text-[11px] text-muted-foreground max-w-[220px]">{r.coa}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.dpp_goods)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.dpp_service)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.dpp)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.ppn)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.pph)}</TableCell>
                <TableCell className="num text-right text-xs font-semibold">{n(r.total)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-secondary/50 font-semibold">
              <TableCell colSpan={5}>{fl("total", lang)}</TableCell>
              <TableCell className="num text-right">{n(report.total_dpp_goods)}</TableCell>
              <TableCell className="num text-right">{n(report.total_dpp_service)}</TableCell>
              <TableCell className="num text-right" data-testid="register-total-dpp">{n(report.total_dpp)}</TableCell>
              <TableCell className="num text-right" data-testid="register-total-ppn">{n(report.total_ppn)}</TableCell>
              <TableCell className="num text-right">{n(report.total_pph)}</TableCell>
              <TableCell className="num text-right" data-testid="register-total">{n(report.total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export function EquityView({ report, lang }) {
  return (
    <Card className="p-4 max-w-3xl" data-testid="equity-view">
      <Table>
        <TableHeader><TableRow>
          <TableHead>{fl("equityAcc", lang)}</TableHead><TableHead className="text-right">{fl("opening", lang)}</TableHead>
          <TableHead className="text-right">{fl("additions", lang)}</TableHead><TableHead className="text-right">{fl("reductions", lang)}</TableHead>
          <TableHead className="text-right">{fl("ending", lang)}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {(report.rows || []).map((r, i) => (
            <TableRow key={i} data-testid={`equity-row-${r.account_code}`}>
              <TableCell className="text-sm">{r.account_code} {r.account_name}</TableCell>
              <TableCell className="num text-right text-xs">{n(r.beginning)}</TableCell>
              <TableCell className="num text-right text-xs text-emerald-600">{n(r.additions)}</TableCell>
              <TableCell className="num text-right text-xs text-rose-600">{n(r.reductions)}</TableCell>
              <TableCell className="num text-right text-xs font-semibold">{n(r.ending)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell className="text-sm">{fl("netIncome", lang)}</TableCell><TableCell />
            <TableCell className="num text-right text-xs text-emerald-600">{report.net_income >= 0 ? n(report.net_income) : "—"}</TableCell>
            <TableCell className="num text-right text-xs text-rose-600">{report.net_income < 0 ? n(-report.net_income) : "—"}</TableCell>
            <TableCell className="num text-right text-xs font-semibold">{n(report.net_income)}</TableCell>
          </TableRow>
          <TableRow className="bg-secondary/50 font-semibold border-t-2 border-primary">
            <TableCell>{fl("totalEquityEnd", lang)}</TableCell>
            <TableCell className="num text-right">{n(report.total_beginning)}</TableCell>
            <TableCell className="num text-right">{n(report.total_additions)}</TableCell>
            <TableCell className="num text-right">{n(report.total_reductions)}</TableCell>
            <TableCell className="num text-right" data-testid="equity-total-ending">{n(report.total_ending)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <SakNote lang={lang} />
    </Card>
  );
}

function PartyIds({ p, lang, testid }) {
  const id = p.npwp || p.nik;
  if (id) return <span className="ml-2 text-[11px] font-normal text-muted-foreground num" data-testid={testid}>{p.npwp ? "NPWP" : "NIK"} {id} · ID TKU {p.id_tku || "—"}</span>;
  if (!p.tax_id_missing) return null;
  return <Badge variant="outline" className="ml-2 text-[10px] border-amber-400 text-amber-700 dark:text-amber-300" data-testid={testid}><AlertTriangle className="h-3 w-3 mr-1" />{fl("taxIdShort", lang)}</Badge>;
}

function TaxSection({ s, kind, mode, lang, L }) {
  const drill = useDrill();
  const taxLbl = kind === "ppn" ? fl("ppn", lang) : fl("pph", lang);
  const pph = kind === "pph";
  const cols = pph ? 8 : 7;
  return (
    <div data-testid={`${kind}-sec-${s.key}`}>
      <h3 className="font-head font-semibold text-sm mb-2">{s.label}</h3>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("docNo", lang)}</TableHead><TableHead>{fl("type", lang)}</TableHead>{pph && <TableHead>{fl("kop", lang)}</TableHead>}<TableHead>{fl("desc", lang)}</TableHead>
            <TableHead className="text-right">{fl("dpp", lang)}</TableHead><TableHead className="text-right">{fl("rate", lang)}</TableHead><TableHead className="text-right">{taxLbl}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {s.parties.length === 0 && <TableRow><TableCell colSpan={cols}><Empty lang={lang} /></TableCell></TableRow>}
            {s.parties.map((p, pi) => (
              <Fragment key={p.party}>
                {mode !== "summary" && <TableRow className="bg-secondary/60" data-testid={`${kind}-party-${s.key}-${pi}`}><TableCell colSpan={cols} className="font-semibold text-xs">{p.party} <span className="text-muted-foreground font-normal">· {p.count}</span><PartyIds p={p} lang={lang} testid={`${kind}-party-ids-${s.key}-${pi}`} /></TableCell></TableRow>}
                {mode !== "summary" && p.rows.map((r, i) => (
                  <TableRow key={`${r.doc_id}-${r.type}-${i}`} className="cursor-pointer hover:bg-secondary/50" data-testid={`${kind}-row-${s.key}-${pi}-${i}`}
                    onClick={() => drill && drill({ doc: r.doc_id, title: `${r.doc_no || r.type} · ${p.party}` })}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}</TableCell><TableCell className="text-xs">{r.doc_no || "—"}</TableCell>
                    <TableCell className="text-xs">{r.type}</TableCell>
                    {pph && <TableCell className="text-xs num whitespace-nowrap" title={r.kop_label} data-testid={`pph-kop-${s.key}-${pi}-${i}`}>{r.kop || <span className="text-amber-600">{fl("kopNone", lang)}</span>}</TableCell>}
                    <TableCell className="text-xs max-w-[260px] truncate">{r.description}</TableCell>
                    <TableCell className="num text-right text-xs">{n(r.dpp)}</TableCell><TableCell className="num text-right text-xs">{r.rate}%</TableCell><TableCell className="num text-right text-xs">{n(r.tax)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold" data-testid={`${kind}-subtotal-${s.key}-${pi}`}>
                  <TableCell colSpan={cols - 3} className="text-xs">{L.subtotal} {p.party}{mode === "summary" && <PartyIds p={p} lang={lang} testid={`${kind}-sum-ids-${s.key}-${pi}`} />}</TableCell><TableCell className="num text-right text-xs">{n(p.dpp)}</TableCell><TableCell /><TableCell className="num text-right text-xs">{n(p.tax)}</TableCell>
                </TableRow>
              </Fragment>
            ))}
            <TableRow className="bg-primary/10 font-bold">
              <TableCell colSpan={cols - 3} className="text-sm">{L.grand_total} {s.label}</TableCell>
              <TableCell className="num text-right" data-testid={`${kind}-gt-dpp-${s.key}`}>{n(s.dpp)}</TableCell><TableCell />
              <TableCell className="num text-right" data-testid={`${kind}-gt-tax-${s.key}`}>{n(s.tax)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function TaxRecapView({ report, mode, lang, kind }) {
  const L = report.labels;
  return (
    <Card className="p-4 space-y-5" data-testid={`${kind}-view`}>
      {report.sections.map((s) => <TaxSection key={s.key} s={s} kind={kind} mode={mode} lang={lang} L={L} />)}
      <div className="flex items-center justify-between flex-wrap gap-2 border-t-2 border-primary pt-3 text-base font-bold">
        <span>{L[kind === "ppn" ? "ppn_net" : "pph_net"]} <Badge variant="outline" className="ml-2" data-testid={`${kind}-status`}>{report.status_label}</Badge></span>
        <span className={`num ${report.net > 0 ? "text-rose-600" : report.net < 0 ? "text-emerald-600" : ""}`} data-testid={`${kind}-net`}>{n(report.net)}</span>
      </div>
      {kind === "pph" && <p className="text-xs text-muted-foreground">{fl("pphNote", lang)}</p>}
    </Card>
  );
}

function IdCells({ r, lang, testid }) {
  return (
    <>
      <TableCell className="text-xs num whitespace-nowrap" data-testid={`${testid}-npwp`}>
        {r.npwp || r.nik || <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3 w-3 mr-1" />{fl("taxIdShort", lang)}</Badge>}
      </TableCell>
      <TableCell className="text-xs num whitespace-nowrap" data-testid={`${testid}-tku`}>{r.id_tku || "—"}</TableCell>
      <TableCell className="text-xs num whitespace-nowrap" title={r.kop_label} data-testid={`${testid}-kop`}>{r.kop || <span className="text-amber-600">{fl("kopNone", lang)}</span>}</TableCell>
    </>
  );
}

function SptWarn({ report, lang }) {
  if (!report.missing_tax_id && !report.missing_kop) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2 flex-wrap" data-testid="spt-incomplete">
      <AlertTriangle className="h-4 w-4" />{fl("incomplete", lang)}:
      {report.missing_tax_id > 0 && <span><b className="num" data-testid="spt-missing-taxid">{report.missing_tax_id}</b> {fl("sptMissing", lang)}</span>}
      {report.missing_kop > 0 && <span>· <b className="num" data-testid="spt-missing-kop">{report.missing_kop}</b> {fl("sptMissingKop", lang)}</span>}
    </div>
  );
}

export function SPTView({ report, lang }) {
  const H = (k, right) => <TableHead className={right ? "text-right" : ""}>{fl(k, lang)}</TableHead>;
  return (
    <div className="space-y-4" data-testid="spt-view">
      <SptWarn report={report} lang={lang} />
      <Card className="p-4">
        <h3 className="font-head font-semibold mb-2">{fl("sptUni", lang)}</h3>
        <div className="overflow-auto">
          <Table>
            <TableHeader><TableRow>{H("masa")}{H("party")}{H("npwpNik")}{H("idTku")}{H("kop")}{H("type")}{H("noBukti")}{H("dpp", 1)}{H("rate", 1)}{H("pph", 1)}</TableRow></TableHeader>
            <TableBody>
              {(!report.unifikasi || report.unifikasi.length === 0) && <TableRow><TableCell colSpan={10} className="text-center py-6 text-muted-foreground text-sm">{fl("sptEmpty1", lang)}</TableCell></TableRow>}
              {(report.unifikasi || []).map((r, i) => (
                <TableRow key={i} data-testid={`spt-uni-row-${i}`}>
                  <TableCell className="num text-xs">{r.masa}</TableCell><TableCell className="text-xs">{r.lawan_transaksi || "—"}</TableCell>
                  <IdCells r={r} lang={lang} testid={`spt-uni-${i}`} />
                  <TableCell className="text-xs">{r.jenis_pajak}</TableCell><TableCell className="text-xs">{r.no_bukti}</TableCell>
                  <TableCell className="num text-right text-xs">{n(r.dpp)}</TableCell><TableCell className="text-right text-xs">{r.tarif}%</TableCell><TableCell className="num text-right text-xs">{n(r.pph)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-right text-sm font-semibold mt-2 num">{fl("total", lang)}: {n(report.total_unifikasi)}</p>
      </Card>
      <Card className="p-4">
        <h3 className="font-head font-semibold mb-2">{fl("spt21", lang)}</h3>
        <div className="overflow-auto">
          <Table>
            <TableHeader><TableRow>{H("masa")}{H("empName")}{H("npwpNik")}{H("idTku")}{H("kop")}<TableHead>PTKP</TableHead>{H("gross", 1)}{H("rate", 1)}<TableHead className="text-right">PPh 21</TableHead></TableRow></TableHeader>
            <TableBody>
              {(!report.pph21 || report.pph21.length === 0) && <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground text-sm">{fl("sptEmpty2", lang)}</TableCell></TableRow>}
              {(report.pph21 || []).map((r, i) => (
                <TableRow key={i} data-testid={`spt-21-row-${i}`}>
                  <TableCell className="num text-xs">{r.masa}</TableCell><TableCell className="text-xs">{r.nama}</TableCell>
                  <IdCells r={r} lang={lang} testid={`spt-21-${i}`} />
                  <TableCell className="text-xs">{r.ptkp}</TableCell><TableCell className="num text-right text-xs">{n(r.bruto)}</TableCell>
                  <TableCell className="text-right text-xs">{r.tarif}%</TableCell><TableCell className="num text-right text-xs">{n(r.pph)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-right text-sm font-semibold mt-2 num">{fl("total", lang)} PPh 21: {n(report.total_pph21)}</p>
      </Card>
      <p className="text-xs text-muted-foreground">{fl("sptNote", lang)}</p>
    </div>
  );
}
